/**
 * Microsoft sign-in through MSAL (ENG-6169).
 *
 * MSAL is imported on first use, so a page without the Microsoft button never
 * loads it. One PublicClientApplication per client id, created once; a failed
 * load is forgotten so the next call tries again. Its cache is memory only:
 * the page wants the ID token once, for the api, and keeps nothing.
 *
 * The popup comes back to `/msal-redirect.html`, a page that runs only MSAL's
 * redirect bridge, which hands the response to this window and closes.
 */
import type { IPublicClientApplication } from '@azure/msal-browser';

export const MICROSOFT_AUTHORITY = 'https://login.microsoftonline.com/common';
export const MICROSOFT_REDIRECT_PATH = '/msal-redirect.html';
const SCOPES = ['openid', 'profile', 'email'];
// MSAL gives the popup 60 s by default, too short for a first sign-in that
// stops on Microsoft's consent screen; past it the sign-in fails.
const POPUP_TIMEOUT_MS = 5 * 60_000;

/** The user closed the popup, or the browser blocked it: nothing to report. */
export class MicrosoftSignInCancelled extends Error {
  constructor() {
    super('Microsoft sign-in was cancelled');
    this.name = 'MicrosoftSignInCancelled';
  }
}

export interface MicrosoftIdentity {
  /**
   * Opens Microsoft's popup and resolves with the ID token (a credential:
   * never log it). Call it straight from the click: the popup opens before
   * the first await, which is what keeps browsers from blocking it.
   */
  signIn(): Promise<string>;
}

const loads = new Map<string, Promise<MicrosoftIdentity>>();

async function createIdentity(clientId: string): Promise<MicrosoftIdentity> {
  const { BrowserAuthErrorCodes, createStandardPublicClientApplication } =
    await import('@azure/msal-browser');
  const cancelled = new Set<string>([
    BrowserAuthErrorCodes.userCancelled,
    BrowserAuthErrorCodes.popupWindowError,
    BrowserAuthErrorCodes.emptyWindowError,
  ]);
  const app: IPublicClientApplication = await createStandardPublicClientApplication({
    auth: {
      clientId,
      authority: MICROSOFT_AUTHORITY,
      redirectUri: `${window.location.origin}${MICROSOFT_REDIRECT_PATH}`,
    },
    cache: { cacheLocation: 'memoryStorage' },
    system: { popupBridgeTimeout: POPUP_TIMEOUT_MS },
  });
  return {
    signIn: () =>
      // A reload or navigation while a popup was open leaves this tab's
      // interaction_in_progress flag behind; overriding it keeps Microsoft
      // sign-in usable. The button allows one popup at a time anyway.
      app
        .loginPopup({
          scopes: SCOPES,
          prompt: 'select_account',
          overrideInteractionInProgress: true,
        })
        .then(
          (result) => result.idToken,
          (err: unknown) => {
            const code = (err as { errorCode?: unknown } | null)?.errorCode;
            throw typeof code === 'string' && cancelled.has(code)
              ? new MicrosoftSignInCancelled()
              : err;
          }
        ),
  };
}

export function loadMicrosoftIdentity(clientId: string): Promise<MicrosoftIdentity> {
  let loading = loads.get(clientId);
  if (!loading) {
    const attempt = createIdentity(clientId);
    attempt.catch(() => {
      if (loads.get(clientId) === attempt) loads.delete(clientId);
    });
    loads.set(clientId, attempt);
    loading = attempt;
  }
  return loading;
}

/** Test seam: forget every load, as a fresh page would. */
export function resetMicrosoftIdentityForTests(): void {
  loads.clear();
}
